import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'

import { useToastContext } from '../../../../../../contexts'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { emailRegex } from '../../../../../../utils/regex'
import { useConfigContext } from '../../../../../ui-config'
import { useAllSuppliers } from '../../../../suppliers/data'
import {
  RoleAssignmentDto,
  useAllRoleSets,
  useAllRoles,
  useRoleAssignmentById,
} from '../../../data'
import { requestPutRoleAssignmentById } from '../../../data/api'
import { EditAssignee } from './EditAssignee'

interface FormValues extends RoleAssignmentDto {}
export type FormFieldName = keyof FormValues

interface Props {
  id: string
  setIsRequestRunning: (params: boolean) => void
  visible: boolean
  onHide: () => void
  mutate: () => void
}
export const EditAssigneeContainer: React.FC<Props> = ({
  id,
  setIsRequestRunning,
  visible,
  onHide,
  mutate,
}) => {
  const { t } = useTranslation()
  const { data: roleAssignment, isLoading } = useRoleAssignmentById(id)
  const { data: roleSets } = useAllRoleSets(true)
  const { data: suppliers } = useAllSuppliers()
  const { data: allRoles } = useAllRoles()

  const { uiConfig: config } = useConfigContext()
  const { showToast } = useToastContext()

  const projectRoles =
    allRoles?.filter(role => role.isProjectScope === true).map(r => r.name) ||
    []

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      role: roleAssignment?.role || '',
      assignee: roleAssignment?.assignee || '',
      project: roleAssignment?.project || '',
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'assignee',
          config: config.selfservices.roleAssignments.editAssignee.assignee,
        },
        {
          fieldName: 'project',
          config: config.selfservices.roleAssignments.editAssignee.project,
        },
        {
          fieldName: 'role',
          config: config.selfservices.roleAssignments.editAssignee.role,
        },
      ]

      if (!data.assignee) errors.assignee = 'Assignee is required.'

      if (!emailRegex.test(data.assignee as string))
        errors.assignee = 'Must be a valid email address.'

      if (!data.role) errors.role = 'Role is required'

      if (projectRoles?.includes(data.role) && !data.project)
        errors.project = 'Project ist required for this role.'

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async data => {
      try {
        onHide()
        setIsRequestRunning(true)

        formik.resetForm()

        if (!projectRoles.includes(data.role)) {
          data.project = ''
        }

        await submitForm(data)

        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'roleAssignments.table.edit.dialog.save.messages.success.summary',
          ),
          detail: t(
            'roleAssignments.table.edit.dialog.save.messages.success.detail',
            { email: data.assignee },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'roleAssignments.table.edit.dialog.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
        mutate()
      }
    },
  })

  const submitForm = async (formData: FormValues) => {
    const newData: RoleAssignmentDto = produce(roleAssignment!, draft => {
      draft.assignee = formData.assignee
      draft.project = formData.project
      draft.role = formData.role
    })
    await requestPutRoleAssignmentById(id, newData)
  }

  const isFormFieldInvalid = (name: FormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <EditAssignee
      formik={formik}
      isFormFieldInvalid={isFormFieldInvalid}
      getFormErrorMessage={getFormErrorMessage}
      config={config.selfservices.roleAssignments.editAssignee}
      roleSets={roleSets}
      suppliers={suppliers}
      projectRoles={projectRoles}
      visible={visible}
      isLoading={isLoading}
      onHide={onHide}
    />
  )
}
